import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Card,
  CardContent,
  Breadcrumbs,
  Button,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  LinearProgress,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  Cable as CableIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  InfoOutlined as InfoIcon,
  CheckCircleOutline as VerifyIcon,
  SwapHoriz as SwapIcon,
  Assignment as RequestIcon,
  NetworkCheck as MonitorIcon,
  AutoAwesome as SuggestIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import axios from '../api/axios';
import { devicesAPI } from '../api/devices';
import { deviceTypesAPI } from '../api/deviceTypes';
import { locationsAPI } from '../api/locations';
import { characteristicsAPI } from '../api/characteristics';
import NetworkInterfaceManager from '../components/NetworkInterfaceManager';
import DeviceSoftwareTab from '../components/DeviceSoftwareTab';
import DeviceUsersTab from '../components/DeviceUsersTab';
import { useAuth } from '../context/AuthContext';
import { CreateDialog } from './ChangeRequests';
import {
  History as HistoryIcon,
  Create as CreateIcon,
  Edit as EditIconHistory,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon
} from '@mui/icons-material';
import { Collapse, Paper } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts';

// Section Header with Info Tooltip Component
const SectionHeader = ({ title, tooltip, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {icon}
    <Typography variant="h6">{title}</Typography>
    {tooltip && (
      <Tooltip title={tooltip} arrow placement="top">
        <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
      </Tooltip>
    )}
  </Box>
);

// Live-metric formatting helpers (Обзор)
const fmtUptime = (sec) => {
  if (sec == null) return '—';
  const d = Math.floor(sec / 86400), h = Math.floor((sec % 86400) / 3600), m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d} дн ${h} ч`;
  if (h > 0) return `${h} ч ${m} мин`;
  return `${m} мин`;
};
const METRIC_TIPS = {
  disk_free_gb: 'Сколько свободного места осталось на системном диске.',
  disk_free_pct: 'Доля свободного места на диске.',
  cpu_pct: 'Текущая загрузка процессора.',
  ram_used_pct: 'Сколько оперативной памяти сейчас занято.',
  uptime_sec: 'Сколько времени устройство работает без перезагрузки.',
};

// Simple SVG Mini-Map Component
const ConnectionMiniMap = ({ graphData, navigate }) => {
  if (!graphData || !graphData.devices || graphData.devices.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography color="text.secondary">Нет данных для отображения</Typography>
      </Box>
    );
  }

  const { devices, connections } = graphData;

  // SVG dimensions
  const width = 700;
  const height = 400;
  const centerX = width / 2;
  const centerY = height / 2;

  // Node dimensions
  const nodeWidth = 120;
  const nodeHeight = 80;

  // Calculate positions for each device
  const devicePositions = {};
  const devicesByLevel = {};

  devices.forEach(d => {
    if (!devicesByLevel[d.level]) devicesByLevel[d.level] = [];
    devicesByLevel[d.level].push(d);
  });

  // Position devices in radial layout
  const levelRadii = { 0: 0, 1: 160, 2: 280 };

  Object.entries(devicesByLevel).forEach(([level, levelDevices]) => {
    const radius = levelRadii[level] || 150;
    const angleStep = (2 * Math.PI) / Math.max(levelDevices.length, 1);
    const startAngle = -Math.PI / 2; // Start from top

    levelDevices.forEach((device, index) => {
      let x, y;
      if (parseInt(level) === 0) {
        x = centerX;
        y = centerY;
      } else {
        const angle = startAngle + angleStep * index;
        x = centerX + radius * Math.cos(angle);
        y = centerY + radius * Math.sin(angle);
      }
      devicePositions[device.id] = { x, y, device };
    });
  });

  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} style={{ background: '#fafafa' }}>
      {/* Draw connections first (so they're behind nodes) */}
      {connections.map(conn => {
        // Use device_1_id and device_2_id (undirected connection)
        const pos1 = devicePositions[conn.device_1_id] || devicePositions[conn.source_device_id];
        const pos2 = devicePositions[conn.device_2_id] || devicePositions[conn.target_device_id];

        if (!pos1 || !pos2) return null;

        // Calculate line from edge of one node to edge of another
        const dx = pos2.x - pos1.x;
        const dy = pos2.y - pos1.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance === 0) return null;

        // Offset from center to edge of nodes
        const nodeOffset = 45;

        const startX = pos1.x + (dx / distance) * nodeOffset;
        const startY = pos1.y + (dy / distance) * nodeOffset;
        const endX = pos2.x - (dx / distance) * nodeOffset;
        const endY = pos2.y - (dy / distance) * nodeOffset;

        // Midpoint for label
        const midX = (startX + endX) / 2;
        const midY = (startY + endY) / 2;

        return (
          <g key={`conn-${conn.id}`}>
            {/* Simple line without arrows - undirected connection */}
            <line
              x1={startX}
              y1={startY}
              x2={endX}
              y2={endY}
              stroke={conn.connection_type_color || '#1976d2'}
              strokeWidth="2"
            />
            {conn.connection_type_name && (
              <g>
                <rect
                  x={midX - 30}
                  y={midY - 10}
                  width="60"
                  height="20"
                  fill="white"
                  rx="4"
                />
                <text
                  x={midX}
                  y={midY + 4}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#666"
                >
                  {conn.connection_type_name.length > 10
                    ? conn.connection_type_name.substring(0, 10) + '...'
                    : conn.connection_type_name}
                </text>
              </g>
            )}
          </g>
        );
      })}

      {/* Draw device nodes */}
      {Object.values(devicePositions).map(({ x, y, device }) => {
        const isCenter = device.level === 0;

        return (
          <g
            key={device.id}
            transform={`translate(${x - nodeWidth/2}, ${y - nodeHeight/2})`}
            style={{ cursor: 'pointer' }}
            onClick={() => navigate(`/devices/${device.id}`)}
          >
            {/* Node background */}
            <rect
              width={nodeWidth}
              height={nodeHeight}
              rx="8"
              fill={isCenter ? '#e3f2fd' : '#ffffff'}
              stroke={isCenter ? '#1976d2' : '#90caf9'}
              strokeWidth={isCenter ? 3 : 2}
              filter="url(#shadow)"
            />

            {/* Device image */}
            {device.device_type_image_url && (
              <image
                href={device.device_type_image_url}
                x={(nodeWidth - 30) / 2}
                y="8"
                width="30"
                height="30"
              />
            )}

            {/* Device name */}
            <text
              x={nodeWidth / 2}
              y={device.device_type_image_url ? 52 : 35}
              textAnchor="middle"
              fontSize={isCenter ? "12" : "11"}
              fontWeight={isCenter ? "bold" : "normal"}
              fill="#1976d2"
            >
              {device.name.length > 12 ? device.name.substring(0, 12) + '...' : device.name}
            </text>

            {/* Device type */}
            <text
              x={nodeWidth / 2}
              y={device.device_type_image_url ? 66 : 50}
              textAnchor="middle"
              fontSize="9"
              fill="#888"
            >
              {device.device_type_name?.length > 14
                ? device.device_type_name.substring(0, 14) + '...'
                : device.device_type_name}
            </text>
          </g>
        );
      })}

      {/* Shadow filter */}
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.15"/>
        </filter>
      </defs>
    </svg>
  );
};

// Device Gallery Component
const DeviceGallerySection = ({ deviceId, expanded, onExpandChange }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    fetchPhotos();
  }, [deviceId]);

  const fetchPhotos = async () => {
    try {
      const response = await axios.get(`/devices/${deviceId}/photos`);
      setPhotos(response.data || []);
    } catch (err) {
      console.error('Error fetching photos:', err);
      setPhotos([]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Допустимые форматы: JPEG, PNG, GIF, WebP');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Максимальный размер файла: 10MB');
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      await axios.post(`/devices/${deviceId}/photos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadDialogOpen(false);
      setSelectedFile(null);
      setPreviewUrl('');
      await fetchPhotos();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка загрузки фотографии');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Удалить эту фотографию?')) return;

    try {
      await axios.delete(`/devices/${deviceId}/photos/${photoId}`);
      await fetchPhotos();
    } catch (err) {
      setError(err.response?.data?.detail || 'Ошибка удаления фотографии');
    }
  };

  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedFile(null);
    setPreviewUrl('');
    setError('');
  };

  return (
    <Accordion expanded={expanded} onChange={onExpandChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
          <SectionHeader
            title={`Фотографии устройства (${photos.length})`}
            tooltip="Фотографии оборудования для визуальной идентификации: общий вид, задняя панель, подключения, серийный номер и т.д."
            icon={<ImageIcon />}
          />
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setUploadDialogOpen(true);
            }}
            size="small"
          >
            Загрузить фото
          </Button>
        </Box>
      </AccordionSummary>
      <AccordionDetails>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {photos.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography color="text.secondary">
              Фотографий нет
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Загрузите фотографии устройства для удобства идентификации
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {photos.map((photo) => (
              <Grid item xs={6} sm={4} md={3} key={photo.id}>
                <Box
                  sx={{
                    position: 'relative',
                    paddingTop: '100%',
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '1px solid #e0e0e0',
                    cursor: 'pointer',
                    '&:hover .delete-btn': {
                      opacity: 1
                    },
                    '&:hover img': {
                      transform: 'scale(1.05)'
                    }
                  }}
                  onClick={() => {
                    setSelectedPhoto(photo);
                    setViewDialogOpen(true);
                  }}
                >
                  <img
                    src={photo.thumbnail_url || photo.url}
                    alt={`Device photo ${photo.id}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.2s'
                    }}
                  />
                  <IconButton
                    className="delete-btn"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(photo.id);
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      backgroundColor: 'rgba(255,255,255,0.9)',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,1)',
                        color: 'error.main'
                      }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Загрузить фотографию</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="photo-upload-input"
            />
            <label htmlFor="photo-upload-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ mt: 1 }}
              >
                Выбрать файл
              </Button>
            </label>

            {previewUrl && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Предпросмотр:
                </Typography>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
                />
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Максимальный размер: 10MB. Форматы: JPEG, PNG, GIF, WebP
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUploadDialog}>Отмена</Button>
            <Button
              onClick={handleUpload}
              variant="contained"
              disabled={!selectedFile || loading}
            >
              {loading ? 'Загрузка...' : 'Загрузить'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Просмотр фотографии</span>
              <IconButton onClick={() => setViewDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedPhoto && (
              <img
                src={selectedPhoto.url}
                alt={`Device photo ${selectedPhoto.id}`}
                style={{ width: '100%', height: 'auto' }}
              />
            )}
          </DialogContent>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
};

// Device History Component
const DeviceHistorySection = ({ deviceId, expanded, onExpandChange }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const rowsPerPage = 10;

  useEffect(() => {
    fetchHistory();
  }, [deviceId, page]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/audit/device/${deviceId}`, {
        params: {
          skip: page * rowsPerPage,
          limit: rowsPerPage
        }
      });
      setHistory(response.data.logs);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Error fetching device history:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleRowExpansion = (logId) => {
    setExpandedRows(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create':
        return <CreateIcon fontSize="small" />;
      case 'update':
        return <EditIconHistory fontSize="small" />;
      case 'delete':
        return <DeleteIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'create':
        return 'success';
      case 'update':
        return 'info';
      case 'delete':
        return 'error';
      default:
        return 'default';
    }
  };

  const getActionLabel = (action) => {
    switch (action) {
      case 'create':
        return 'Создано';
      case 'update':
        return 'Изменено';
      case 'delete':
        return 'Удалено';
      default:
        return action;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const renderChanges = (changes) => {
    if (!changes || Object.keys(changes).length === 0) {
      return <Typography variant="body2" color="textSecondary">Нет изменений</Typography>;
    }

    return (
      <Box sx={{ mt: 1 }}>
        {Object.entries(changes).map(([field, change]) => (
          <Box key={field} sx={{ mb: 1 }}>
            <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
              {field}:
            </Typography>
            {change.old !== undefined && (
              <Typography variant="body2" component="span" sx={{ ml: 1, color: 'error.main' }}>
                {change.old === null ? '(пусто)' : String(change.old)}
              </Typography>
            )}
            <Typography variant="body2" component="span" sx={{ mx: 1 }}>
              →
            </Typography>
            {change.new !== undefined && (
              <Typography variant="body2" component="span" sx={{ color: 'success.main' }}>
                {change.new === null ? '(пусто)' : String(change.new)}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  if (history.length === 0 && !loading) {
    return null; // Don't show section if no history
  }

  return (
    <Accordion expanded={expanded} onChange={onExpandChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SectionHeader
          title="История изменений"
          tooltip="Журнал всех изменений устройства: создание, обновление характеристик, добавление ПО, смена локации и т.д. Показывает кто и когда внес изменения"
          icon={<HistoryIcon />}
        />
      </AccordionSummary>
      <AccordionDetails>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="50px"></TableCell>
                    <TableCell>Дата и время</TableCell>
                    <TableCell>Действие</TableCell>
                    <TableCell>Пользователь</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((log) => (
                    <React.Fragment key={log.id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(log.id)}
                          >
                            {expandedRows[log.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{formatDate(log.created_at)}</TableCell>
                        <TableCell>
                          <Chip
                            icon={getActionIcon(log.action)}
                            label={getActionLabel(log.action)}
                            color={getActionColor(log.action)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {log.user ? (
                            <Box>
                              <Typography variant="body2">
                                {log.user.username}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {log.user.full_name || log.user.email}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              Система
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                          <Collapse in={expandedRows[log.id]} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Детали изменений:
                              </Typography>
                              {renderChanges(log.changes)}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {total > rowsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  disabled={page === 0}
                  onClick={(e) => handleChangePage(e, page - 1)}
                  sx={{ mr: 1 }}
                >
                  Назад
                </Button>
                <Typography sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                  Страница {page + 1} из {Math.ceil(total / rowsPerPage)}
                </Typography>
                <Button
                  disabled={(page + 1) * rowsPerPage >= total}
                  onClick={(e) => handleChangePage(e, page + 1)}
                >
                  Вперед
                </Button>
              </Box>
            )}
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [device, setDevice] = useState(null);
  const [connections, setConnections] = useState([]);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    device_type_id: '',
    location_id: null,
    description: '',
    characteristic_values: {}
  });
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [characteristics, setCharacteristics] = useState([]);
  const [editTabValue, setEditTabValue] = useState(0);
  const [monState, setMonState] = useState(null);   // /monitoring-state aggregate
  const [crInitial, setCrInitial] = useState(null);  // pre-seed for CreateDialog
  const [mapLevels, setMapLevels] = useState(2);     // mini-map neighbourhood depth
  const [suggestions, setSuggestions] = useState([]); // suggested connections
  const [pageTab, setPageTab] = useState('overview'); // active device-page tab
  const [compMenu, setCompMenu] = useState(null);     // per-component actions menu
  const [driftEvents, setDriftEvents] = useState([]); // open reality-drift (scan≠entered) for this device
  const [bindings, setBindings] = useState([]);       // observation source bindings
  const [sources, setSources] = useState([]);         // available integration sources
  const [bindDlg, setBindDlg] = useState(false);
  const [bindForm, setBindForm] = useState({ source_id: '', external_ref: '' });
  const [nicPorts, setNicPorts] = useState(null);    // physical NIC<->switch-port view (И5)
  const [enrollInfo, setEnrollInfo] = useState(null); // agent enrollment status (И6)
  const [agentDlg, setAgentDlg] = useState(false);
  const [agentToken, setAgentToken] = useState('');   // shown ONCE after (re)issue
  const [agentAvail, setAgentAvail] = useState({});    // which agent files are downloadable
  const [netInfo, setNetInfo] = useState(null);        // agent network view (Сеть)
  const [scanChars, setScanChars] = useState(null);    // hardware → characteristics suggestion
  const canCreateCR = user?.is_superuser || user?.permissions?.includes('change_requests.create');
  const canEditDevices = user?.is_superuser || user?.permissions?.includes('devices.edit');

  // Accordion expansion states - load from localStorage or default to collapsed
  const [expanded, setExpanded] = useState(() => {
    const saved = localStorage.getItem(`device-${id}-accordion-state`);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error parsing saved accordion state:', e);
      }
    }
    // Default: all sections collapsed
    return {
      basic: false,
      characteristics: false,
      connections: false,
      interfaces: false,
      software: false,
      users: false,
      map: false,
      gallery: false,
      history: false,
      metadata: false
    };
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(prev => {
      const newState = { ...prev, [panel]: isExpanded };
      // Save to localStorage
      localStorage.setItem(`device-${id}-accordion-state`, JSON.stringify(newState));
      return newState;
    });
  };

  // Connection dialog states
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const [allDevices, setAllDevices] = useState([]);
  const [connectionTypes, setConnectionTypes] = useState([]);
  const [newConnection, setNewConnection] = useState({
    target_device_id: null,
    connection_type_id: null
  });

  useEffect(() => {
    fetchDevice();
    fetchConnections();
    fetchGraphData();
    fetchMonitoringState();
    fetchSuggestions();
    fetchBindings();
    fetchSources();
    fetchNicPorts();
    fetchEnroll();
    fetchAgentAvail();
    fetchNetwork();
    fetchScanChars();
    fetchDrift();
    fetchDeviceTypesAndLocations();
  }, [id]);

  // If the active tab isn't applicable to this device type, fall back to «Обзор»
  useEffect(() => {
    const secs = device?.device_type?.applicable_sections;
    if (Array.isArray(secs) && secs.length && pageTab !== 'overview' && !secs.includes(pageTab)) {
      setPageTab('overview');
    }
  }, [device, pageTab]);

  const fetchDeviceTypesAndLocations = async () => {
    try {
      const [types, locs, chars] = await Promise.all([
        deviceTypesAPI.getAll(),
        locationsAPI.getAll(),
        characteristicsAPI.getAll()
      ]);
      setDeviceTypes(types);
      setLocations(locs);
      setCharacteristics(chars);
    } catch (err) {
      console.error('Error loading device types, locations, and characteristics:', err);
    }
  };

  const fetchDevice = async () => {
    try {
      const response = await axios.get(`/devices/${id}`);
      setDevice(response.data);
      setError('');   // clear any stale error (e.g. after navigating from a deleted device)
    } catch (err) {
      setError('Ошибка загрузки устройства');
      console.error(err);
    }
  };

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`/devices/${id}/connections`);
      setConnections(response.data);
    } catch (err) {
      console.error('Error fetching connections:', err);
    }
  };

  const fetchGraphData = async (levels) => {
    try {
      const lv = levels || mapLevels;
      const response = await axios.get(`/devices/${id}/connections-graph?levels=${lv}`);
      setGraphData(response.data);
    } catch (err) {
      console.error('Error fetching graph data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonitoringState = async () => {
    try {
      const r = await axios.get(`/devices/${id}/monitoring-state`);
      setMonState(r.data);
    } catch (err) {
      // monitoring is optional — ignore
    }
  };

  const fetchSuggestions = async () => {
    try {
      const r = await axios.get(`/connections/suggestions?device_id=${id}`);
      setSuggestions(r.data.suggestions || []);
    } catch (err) {
      // suggestions are optional — ignore
    }
  };

  const acceptSuggestion = async (s) => {
    try {
      await axios.post('/connections/suggestions/accept', {
        source_device_id: s.source_device_id,
        target_device_id: s.target_device_id,
        subnet_id: s.subnet_id || null,
      });
      fetchConnections();
      fetchGraphData();
      fetchSuggestions();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  };

  const fetchBindings = async () => {
    try { const r = await axios.get(`/devices/${id}/bindings`); setBindings(r.data || []); } catch (e) { /* optional */ }
  };
  const fetchNicPorts = async () => {
    try { const r = await axios.get(`/devices/${id}/nic-ports`); setNicPorts(r.data); } catch (e) { /* optional */ }
  };
  const fetchNetwork = async () => {
    try { const r = await axios.get(`/devices/${id}/network`); setNetInfo(r.data); } catch (e) { /* optional */ }
  };
  const fetchScanChars = async () => {
    try { const r = await axios.get(`/devices/${id}/scan-characteristics`); setScanChars(r.data); } catch (e) { /* optional */ }
  };
  const applyScanChars = async () => {
    try {
      await axios.post(`/devices/${id}/scan-characteristics/apply`);
      setError('');
      fetchDevice(); fetchScanChars();
    } catch (e) { setError('Не удалось создать характеристики: ' + (e.response?.data?.detail || e.message)); }
  };
  const applyScanFile = async (file) => {
    try {
      const payload = JSON.parse(await file.text());
      await axios.post(`/devices/${id}/apply-scan`, payload);
      setError('');
      fetchDevice(); fetchMonitoringState(); fetchNetwork(); fetchNicPorts(); fetchDrift();
    } catch (e) {
      setError('Не удалось загрузить скан: ' + (e.response?.data?.detail || e.message || 'неверный файл'));
    }
  };
  // Reality-drift: observation (scan) differs from entered data → manager confirms/denies
  const fetchDrift = async () => {
    try {
      const r = await axios.get('/drift/', { params: { drift_source: 'reality', limit: 200 } });
      const items = (r.data?.items || []).filter(
        (e) => e.entity_type === 'device' && e.entity_id === Number(id) &&
               ['open', 'in_review'].includes(e.status));
      setDriftEvents(items);
    } catch (e) { /* drift optional */ }
  };
  const driftAction = async (ev, action) => {
    try {
      let body = {};
      if (action === 'process') {
        const reason = window.prompt('Объясните расхождение (почему данные изменились):');
        if (!reason || !reason.trim()) return;
        body = { reason: reason.trim() };
      } else if (action === 'dismiss') {
        const reason = window.prompt('Причина отклонения (почему это НЕ считается изменением):');
        if (!reason || !reason.trim()) return;
        body = { reason: reason.trim() };
      }
      await axios.post(`/drift/${ev.id}/${action}`, body);
      setError('');
      fetchDrift(); fetchDevice();
    } catch (e) { setError('Не удалось обработать расхождение: ' + (e.response?.data?.detail || e.message)); }
  };
  const bindNicPort = async (nic) => {
    if (!nic.fdb_location) return;
    try {
      await axios.post(`/devices/${id}/nic-ports/bind`, {
        interface_id: nic.interface_id,
        switch_device_id: nic.fdb_location.switch_device_id,
        port: nic.fdb_location.port,
      });
      fetchNicPorts(); fetchConnections(); fetchGraphData();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const fetchEnroll = async () => {
    try { const r = await axios.get(`/agent/enroll/${id}`); setEnrollInfo(r.data); } catch (e) { /* optional */ }
  };
  const fetchAgentAvail = async () => {
    try { const r = await axios.get('/agent/download'); setAgentAvail(r.data || {}); } catch (e) { /* optional */ }
  };
  const downloadAgent = async (kind, filename) => {
    try {
      const r = await axios.get(`/agent/download/${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.status === 404 ? 'Файл агента ещё не собран на сервере' : (e.message || 'Ошибка скачивания'));
    }
  };
  const issueAgentToken = async () => {
    try {
      const r = await axios.post(`/agent/enroll/${id}`);
      setAgentToken(r.data.token || ''); setAgentDlg(true); fetchEnroll();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  const revokeAgentToken = async () => {
    try { await axios.delete(`/agent/enroll/${id}`); fetchEnroll(); } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  const copyText = (t) => { try { navigator.clipboard.writeText(t); } catch (e) { /* noop */ } };
  const downloadConfig = (text) => {
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'inventory_agent.json'; a.click();
    URL.revokeObjectURL(url);
  };
  const fetchSources = async () => {
    try { const r = await axios.get('/integrations/'); setSources(r.data || []); } catch (e) { /* optional */ }
  };
  const saveBinding = async () => {
    try {
      await axios.post(`/devices/${id}/bindings`, { source_id: bindForm.source_id, external_ref: bindForm.external_ref || null });
      setBindDlg(false); setBindForm({ source_id: '', external_ref: '' }); fetchBindings();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  const deleteBinding = async (bid) => {
    try { await axios.delete(`/devices/${id}/bindings/${bid}`); fetchBindings(); } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  // Export device in various formats
  const handleExport = async (format) => {
    const formatExtensions = {
      'json': { ext: 'json', mime: 'application/json' },
      'txt': { ext: 'txt', mime: 'text/plain' },
      'csv': { ext: 'csv', mime: 'text/csv' },
      'excel': { ext: 'xlsx', mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      'pdf': { ext: 'pdf', mime: 'application/pdf' }
    };

    const { ext } = formatExtensions[format];

    try {
      const response = await fetch(`/api/reports/devices/${id}/export/${format}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `device_${id}_export.${ext}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Ошибка экспорта');
      }
    } catch (err) {
      setError('Ошибка экспорта: ' + err.message);
    }
    setExportAnchorEl(null);
  };

  // Load devices and connection types when dialog opens
  useEffect(() => {
    if (connectionDialogOpen) {
      fetchAllDevices();
      fetchConnectionTypes();
    }
  }, [connectionDialogOpen]);

  const fetchAllDevices = async () => {
    try {
      const response = await axios.get('/devices/');
      // Exclude current device from list
      const devices = response.data.items || response.data;
      setAllDevices(devices.filter(d => d.id !== parseInt(id)));
    } catch (err) {
      console.error('Error fetching devices:', err);
    }
  };

  const fetchConnectionTypes = async () => {
    try {
      const response = await axios.get('/connection-types/');
      setConnectionTypes(response.data);
    } catch (err) {
      console.error('Error fetching connection types:', err);
    }
  };

  const handleCreateConnection = async () => {
    if (!newConnection.target_device_id || !newConnection.connection_type_id) {
      alert('Пожалуйста, заполните все поля');
      return;
    }

    try {
      await axios.post('/connections/', {
        source_device_id: parseInt(id),
        target_device_id: newConnection.target_device_id,
        connection_type_id: newConnection.connection_type_id
      });

      // Refresh connections
      fetchConnections();
      fetchGraphData();

      // Reset and close dialog
      setConnectionDialogOpen(false);
      setNewConnection({
        target_device_id: null,
        connection_type_id: null
      });
    } catch (err) {
      console.error('Error creating connection:', err);
      alert('Ошибка при создании подключения');
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это подключение?')) {
      return;
    }

    try {
      await axios.delete(`/connections/${connectionId}`);

      // Refresh connections
      fetchConnections();
      fetchGraphData();
    } catch (err) {
      console.error('Error deleting connection:', err);
      alert('Ошибка при удалении подключения');
    }
  };

  const handleCharacteristicChange = (characteristicId, value) => {
    setEditFormData({
      ...editFormData,
      characteristic_values: {
        ...editFormData.characteristic_values,
        [characteristicId]: value
      }
    });
  };

  const renderCharacteristicInput = (dtc) => {
    const char = characteristics.find(c => c.id === dtc.characteristic_id);
    if (!char) return null;

    const value = editFormData.characteristic_values[dtc.characteristic_id] || char.default_value || '';
    const dataType = char.data_type;

    // Check if there's a saved value from the device
    const savedValue = device?.characteristic_values?.find(cv => cv.characteristic_id === dtc.characteristic_id)?.value;
    const hasValue = savedValue && savedValue !== '';
    const helperText = hasValue
      ? `${char.description || ''}${char.description ? ' • ' : ''}✓ Заполнено`
      : `${char.description || ''}${char.description ? ' • ' : ''}Не заполнено`;

    let inputComponent;

    switch (dataType?.base_type) {
      case 'enum':
        inputComponent = (
          <TextField
            fullWidth
            select
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
          >
            {dataType.enum_values && dataType.enum_values.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        );
        break;

      case 'boolean':
        inputComponent = (
          <TextField
            fullWidth
            select
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
          >
            <MenuItem value="true">Да</MenuItem>
            <MenuItem value="false">Нет</MenuItem>
          </TextField>
        );
        break;

      case 'date':
        inputComponent = (
          <TextField
            fullWidth
            type="date"
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
            InputLabelProps={{ shrink: true }}
          />
        );
        break;

      case 'datetime':
        inputComponent = (
          <TextField
            fullWidth
            type="datetime-local"
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
            InputLabelProps={{ shrink: true }}
          />
        );
        break;

      case 'integer':
      case 'port':
        inputComponent = (
          <TextField
            fullWidth
            type="number"
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
            placeholder={dataType.placeholder}
          />
        );
        break;

      case 'float':
        inputComponent = (
          <TextField
            fullWidth
            type="number"
            inputProps={{ step: 0.01 }}
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
            placeholder={dataType.placeholder}
          />
        );
        break;

      case 'text':
        inputComponent = (
          <TextField
            fullWidth
            multiline
            rows={3}
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText}
            placeholder={dataType.placeholder}
          />
        );
        break;

      default: // string, ip, mac, cidr, json, etc.
        inputComponent = (
          <TextField
            fullWidth
            label={char.name + (dtc.is_required ? ' *' : '')}
            value={value}
            onChange={(e) => handleCharacteristicChange(dtc.characteristic_id, e.target.value)}
            margin="normal"
            helperText={helperText || dataType?.help_text}
            placeholder={dataType?.placeholder}
          />
        );
    }

    return (
      <Grid item xs={12} md={6} key={dtc.characteristic_id}>
        {inputComponent}
      </Grid>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !device) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Alert severity="error">{error || 'Устройство не найдено'}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/devices')} sx={{ mt: 2 }}>
          Назад к списку
        </Button>
      </Container>
    );
  }

  // Group characteristic VALUES by characteristic, keeping each instance separate
  // (so a specific disk / RAM stick can be acted on individually).
  const charGroups = {};
  device.characteristic_values?.forEach(cv => {
    const char = cv.characteristic;
    if (!char) return;
    if (!charGroups[cv.characteristic_id]) charGroups[cv.characteristic_id] = { char, instances: [] };
    charGroups[cv.characteristic_id].instances.push(cv);
  });
  const charGroupList = Object.values(charGroups);
  charGroupList.forEach(g => g.instances.sort((a, b) => (a.instance_index || 0) - (b.instance_index || 0)));

  // Open the change-request dialog pre-seeded for ONE component instance.
  const openComponentCR = (char, cv, operation) => {
    const word = operation === 'remove' ? 'Снять' : 'Заменить';
    const suffix = char.is_multiple ? ` #${(cv.instance_index || 0) + 1}` : '';
    setCrInitial({
      device,
      category: 'component_change',
      title: `${word}: ${char.name}${suffix} — ${device.name}`,
      item: {
        target_id: device.id,
        target_locator: { characteristic_id: char.id, instance_index: cv.instance_index || 0 },
        operation,
        char_name: char.name,
        char_code: char.code,
        instance_index: cv.instance_index || 0,
        current_value: cv.value || '',
        instance_label: cv.instance_label || '',
      },
    });
  };

  const TAB_LIST = [
    { v: 'overview', label: 'Обзор', tip: 'Текущее состояние: доступность, живые метрики (диск/CPU/память/аптайм) и краткая сводка по устройству.' },
    { v: 'chars', label: 'Характеристики', tip: 'Точный набор характеристик этого типа устройства — железо и система. Диски и MAC — отдельными экземплярами с действиями.' },
    { v: 'network', label: 'Сеть', tip: 'Сетевые интерфейсы, физическое подключение к портам коммутатора, активность, соседи, подключения и карта связей.' },
    { v: 'security', label: 'Безопасность', tip: 'Защита ОС по данным агента: брандмауэр, RDP, антивирус, общие папки, обновления.' },
    { v: 'software', label: 'ПО', tip: 'Установленные программы. Каждая попадает в общий «Каталог ПО» автоматически при сканировании.' },
    { v: 'monitoring', label: 'Наблюдение', tip: 'Чем наблюдается устройство (агент / GLPI / Zabbix / SNMP) и подключение собственного агента на ПК.' },
    { v: 'history', label: 'Журнал', tip: 'Пользователи с доступом, фотографии, история изменений и метаданные записи.' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header with breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <Link to="/devices" style={{ textDecoration: 'none', color: 'inherit' }}>
            Устройства
          </Link>
          <Typography color="text.primary">{device.name}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => navigate('/devices')} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">{device.name}</Typography>
            <Chip
              size="small"
              sx={{ ml: 2 }}
              color={device.last_attested_at && (Date.now() - new Date(device.last_attested_at).getTime()) <= 180 * 86400000 ? 'success' : 'warning'}
              label={device.last_attested_at
                ? `Актуальность: ${new Date(device.last_attested_at).toLocaleDateString('ru-RU')}`
                : 'Актуальность не подтверждалась'}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="success"
              startIcon={<VerifyIcon />}
              onClick={async () => {
                try {
                  await axios.post(`/devices/${device.id}/attest`);
                  fetchDevice();  // refresh in-place (no full reload → no bounce)
                } catch (e) {
                  setError('Ошибка подтверждения актуальности: ' + (e.response?.data?.detail || e.message));
                }
              }}
            >
              Подтвердить актуальность
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
            >
              Экспорт
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem onClick={() => handleExport('json')}>
                📄 JSON (для API/интеграций)
              </MenuItem>
              <MenuItem onClick={() => handleExport('txt')}>
                📝 TXT (для AI агентов)
              </MenuItem>
              <MenuItem onClick={() => handleExport('csv')}>
                📊 CSV (для Excel)
              </MenuItem>
              <MenuItem onClick={() => handleExport('excel')}>
                📋 Excel (форматированный)
              </MenuItem>
              <MenuItem onClick={() => handleExport('pdf')}>
                📑 PDF (документ)
              </MenuItem>
            </Menu>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {
                // Prepare characteristic values
                const charValues = {};
                if (device.characteristic_values && Array.isArray(device.characteristic_values)) {
                  device.characteristic_values.forEach(cv => {
                    charValues[cv.characteristic_id] = cv.value;
                  });
                }

                setEditFormData({
                  name: device.name || '',
                  device_type_id: device.device_type_id || '',
                  location_id: device.location_id || null,
                  description: device.description || '',
                  characteristic_values: charValues
                });
                setEditTabValue(0);
                setEditDialogOpen(true);
              }}
            >
              Редактировать
            </Button>
            {canCreateCR && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<RequestIcon />}
                onClick={() => setCrInitial({ device, category: 'component_change', title: `Изменение: ${device.name}` })}
              >
                Создать заявку
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Сводная полоса: тип / локация / подключения (видна на всех вкладках) */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <Chip size="small" icon={<CategoryIcon />} variant="outlined" color="primary"
              label={`Тип: ${device.device_type?.name || '—'}`} />
        <Chip size="small" icon={<LocationIcon />} variant="outlined" color="success"
              label={`Локация: ${device.location?.name || '—'}`} />
        <Chip size="small" icon={<CableIcon />} variant="outlined" color="info"
              label={`Подключений: ${connections.length}`} />
        {device.is_critical && <Chip size="small" color="error" label="Критичное" />}
      </Box>

      {/* Вкладки страницы устройства */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={pageTab} onChange={(e, v) => setPageTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
          {TAB_LIST.filter((t) => {
            const secs = device.device_type?.applicable_sections;
            // 'overview' всегда; если у типа не задан список разделов — показываем все
            return t.v === 'overview' || !Array.isArray(secs) || secs.length === 0 || secs.includes(t.v);
          }).map((t) => (
            <Tab key={t.v} value={t.v} label={(
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t.label}
                <Tooltip title={t.tip} arrow><InfoIcon sx={{ fontSize: 15, color: 'text.disabled' }} /></Tooltip>
              </Box>
            )} />
          ))}
        </Tabs>
      </Box>

      {/* === МОНИТОРИНГ: состояние (живое) === */}
      {pageTab === 'monitoring' && monState && (() => {
        const fp = monState.fingerprint, pr = monState.profile;
        const rChip = { ok: { c: 'success', t: 'Доступно' }, unreachable: { c: 'error', t: 'Недоступно' },
                        error: { c: 'warning', t: 'Ошибка опроса' } }[monState.reachability]
                      || { c: 'default', t: 'Мониторинг не настроен' };
        const disks = (fp && fp.disks) || [];
        const stat = (label, value) => (
          <Box sx={{ mr: 3, mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
            <Typography variant="body2">{value}</Typography>
          </Box>
        );
        // Live metrics -> bars (disk/cpu/ram get a %; uptime/others plain text). Null-safe.
        const mMap = {};
        (monState.metrics || []).forEach((m) => { mMap[m.key] = m; });
        const num = (k) => { const v = mMap[k] ? mMap[k].value : null; const n = v == null ? null : parseFloat(v); return (n == null || isNaN(n)) ? null : n; };
        const liveMetrics = [];
        const dFree = num('disk_free_gb'), dTotal = num('disk_total_gb'), dPct = num('disk_free_pct');
        if (dTotal != null && dFree != null) {
          liveMetrics.push({ key: 'disk_free_gb', label: 'Свободно на диске', pct: 100 - (dFree / dTotal * 100), text: `${Math.round(dFree)} / ${Math.round(dTotal)} ГБ` });
        } else if (dPct != null) {
          liveMetrics.push({ key: 'disk_free_pct', label: 'Свободно на диске', pct: 100 - dPct, text: `${Math.round(dPct)}% своб.` });
        }
        const cpu = num('cpu_pct');
        if (cpu != null) liveMetrics.push({ key: 'cpu_pct', label: 'Загрузка CPU', pct: cpu, text: `${Math.round(cpu)}%` });
        const ramp = num('ram_used_pct');
        if (ramp != null) liveMetrics.push({ key: 'ram_used_pct', label: 'Память занята', pct: ramp, text: `${Math.round(ramp)}%` });
        const up = num('uptime_sec');
        if (up != null) liveMetrics.push({ key: 'uptime_sec', label: 'Аптайм', text: fmtUptime(up) });
        const _covered = ['disk_free_gb', 'disk_total_gb', 'disk_free_pct', 'cpu_pct', 'ram_used_pct', 'uptime_sec'];
        (monState.metrics || []).forEach((m) => {
          if (!_covered.includes(m.key)) liveMetrics.push({ key: m.key, label: m.label || m.key, text: `${m.value == null ? '—' : m.value}${m.unit ? ' ' + m.unit : ''}` });
        });
        // Compact one-line spec summary (full detail lives in the «Характеристики» tab)
        const summaryParts = [];
        if (fp && fp.hw && fp.hw.cpu) summaryParts.push(fp.hw.cpu);
        if (fp && fp.ram_total_mb) summaryParts.push(`${Math.round(fp.ram_total_mb / 1024)} ГБ ОЗУ`);
        if (fp && fp.os) summaryParts.push(fp.os);
        if (disks.length) summaryParts.push(`${disks.length} диск(ов)`);
        if (fp && fp.software_count) summaryParts.push(`${fp.software_count} программ`);
        return (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <MonitorIcon fontSize="small" color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Состояние (по данным мониторинга)</Typography>
              <Chip size="small" color={rChip.c} label={rChip.t} />
            </Box>
            {(!fp && !pr && !(monState.metrics || []).length) ? (
              <Typography variant="body2" color="text.secondary">
                Нет данных мониторинга. <Link to="/monitoring" style={{ color: '#1976d2' }}>Настроить опрос →</Link>
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                {stat('Последний опрос', pr?.last_run_at ? new Date(pr.last_run_at).toLocaleString('ru-RU') : '—')}
                {liveMetrics.length ? liveMetrics.map((mm, i) => (
                  <Box key={`m-${i}`} sx={{ mr: 3, mb: 0.5, minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">{mm.label}</Typography>
                      {METRIC_TIPS[mm.key] && (
                        <Tooltip title={METRIC_TIPS[mm.key]} arrow><InfoIcon sx={{ fontSize: 13, color: 'text.disabled' }} /></Tooltip>
                      )}
                    </Box>
                    {mm.pct != null ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, mm.pct))}
                          color={mm.pct >= 90 ? 'error' : mm.pct >= 75 ? 'warning' : 'success'}
                          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }} />
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>{mm.text}</Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2">{mm.text}</Typography>
                    )}
                  </Box>
                )) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Живых метрик пока нет (агент их ещё не прислал).</Typography>
                )}
              </Box>
            )}
            {summaryParts.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                Кратко: {summaryParts.join(' · ')}{'  '}
                <span style={{ color: '#1976d2', cursor: 'pointer' }} onClick={() => setPageTab('chars')}>подробнее →</span>
              </Typography>
            )}
            {monState.incidents && monState.incidents.length > 0 && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Влияет на доступность: {monState.incidents.map(i => i.summary).join('; ')}.{' '}
                <Link to="/monitoring" style={{ color: 'inherit' }}>Открыть</Link>
              </Alert>
            )}
          </Paper>
        );
      })()}

      {/* === ОБЗОР: расхождения «скан ≠ внесено» (governance: руководитель подтверждает) === */}
      {pageTab === 'overview' && driftEvents.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: 'warning.main', bgcolor: '#fff8e1' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <MonitorIcon fontSize="small" color="warning" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Расхождения с реальностью ({driftEvents.length})</Typography>
            <Tooltip title="Наблюдение (скан агента) отличается от внесённых данных. Инженер объясняет причину, затем руководитель подтверждает (принять как новую норму) или отклоняет. Система не переписывает данные молча.">
              <InfoIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            </Tooltip>
          </Box>
          {driftEvents.map((ev) => {
            const canProcess = user?.is_superuser || user?.permissions?.includes('drift.process');
            const canConfirm = user?.is_superuser || user?.permissions?.includes('drift.confirm');
            const diff = ev.diff_json || {};
            return (
              <Box key={ev.id} sx={{ mb: 1.5, pb: 1, borderBottom: '1px solid #eee' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" color={ev.status === 'in_review' ? 'info' : 'warning'}
                        label={ev.status === 'in_review' ? 'на подтверждении' : 'новое'} />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{ev.summary}</Typography>
                </Box>
                <Box sx={{ mt: 0.5, ml: 1 }}>
                  {Object.keys(diff).map((k) => (
                    <Typography key={k} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      • {k}: внесено «{String(diff[k]?.expected ?? '—')}» / скан «{String(diff[k]?.observed ?? '—')}»
                    </Typography>
                  ))}
                  {ev.engineer_reason && (
                    <Typography variant="caption" sx={{ display: 'block', fontStyle: 'italic' }}>
                      Объяснение инженера: {ev.engineer_reason}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ mt: 0.5, ml: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {ev.status === 'open' && canProcess && (
                    <Button size="small" variant="outlined" onClick={() => driftAction(ev, 'process')}>Объяснить</Button>
                  )}
                  {ev.status === 'in_review' && canConfirm && (
                    <Button size="small" variant="contained" color="success" onClick={() => driftAction(ev, 'confirm')}>Подтвердить</Button>
                  )}
                  {canConfirm && (
                    <Button size="small" variant="outlined" color="error" onClick={() => driftAction(ev, 'dismiss')}>Отклонить</Button>
                  )}
                  {ev.status === 'open' && !canProcess && !canConfirm && (
                    <Typography variant="caption" color="text.secondary">Ожидает инженера/руководителя</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}

      {/* === НАБЛЮДЕНИЕ: источники === */}
      {pageTab === 'monitoring' && (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <MonitorIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Чем наблюдается (источники)</Typography>
          <Box sx={{ flexGrow: 1 }} />
          {canCreateCR && (
            <Button size="small" variant="outlined" startIcon={<AddIcon />}
              onClick={() => { setBindForm({ source_id: '', external_ref: '' }); setBindDlg(true); }}>
              Привязать источник
            </Button>
          )}
        </Box>
        {bindings.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Не привязано. Укажите, каким источником наблюдать это устройство (агент / GLPI / Zabbix / SNMP) — система будет тянуть данные и мониторить.{' '}
            {sources.length === 0 && <Link to="/integrations" style={{ color: '#1976d2' }}>Сначала добавьте источник →</Link>}
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {bindings.map((b) => (
              <Box key={b.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={b.source_type || '?'} />
                <Typography variant="body2">{b.source_name}</Typography>
                {b.external_ref && <Typography variant="caption" color="text.secondary">ссылка: {b.external_ref}</Typography>}
                {b.last_status && <Chip size="small" variant="outlined" color={b.last_status === 'ok' ? 'success' : 'default'} label={b.last_status} />}
                <Box sx={{ flexGrow: 1 }} />
                {canCreateCR && <IconButton size="small" color="error" onClick={() => deleteBinding(b.id)}><DeleteIcon fontSize="small" /></IconButton>}
              </Box>
            ))}
          </Box>
        )}
      </Paper>
      )}

      {/* === СЕТЬ: физическое подключение (NIC ↔ порты) === */}
      {pageTab === 'network' && nicPorts && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <MonitorIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {nicPorts.is_switch ? 'Карта портов (по FDB коммутатора)' : 'Физическое подключение (сетевые карты → порты)'}
            </Typography>
            <Tooltip title="Кто за каким портом — берётся из таблицы MAC-адресов коммутатора (FDB, метод «ports» по SNMP). «Привязать» фиксирует связь устройства с коммутатором и сохраняет порт.">
              <InfoIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            </Tooltip>
          </Box>

          {nicPorts.is_switch ? (
            (nicPorts.ports || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                FDB ещё не собран. Включите профиль мониторинга с методом «ports» (SNMP) для этого коммутатора — система выучит, какое устройство за каким портом.{' '}
                <Link to="/monitoring" style={{ color: '#1976d2' }}>Настроить →</Link>
              </Typography>
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Портов: {nicPorts.port_count} · известных устройств: {nicPorts.device_count}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {nicPorts.ports.map((p, i) => (
                    <Box key={`p-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={p.port} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{p.mac}</Typography>
                      {p.device_id ? (
                        <Link to={`/devices/${p.device_id}`} style={{ color: '#1976d2' }}>{p.device_name}</Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">неизвестное устройство</Typography>
                      )}
                      <Box sx={{ flexGrow: 1 }} />
                      {p.status === 'recorded' && <Chip size="small" color="success" variant="outlined" label="связь записана" />}
                      {p.status === 'unrecorded' && <Chip size="small" color="warning" variant="outlined" label="связь не записана" />}
                      {p.status === 'unknown_device' && <Chip size="small" variant="outlined" label="не наш MAC" />}
                    </Box>
                  ))}
                </Box>
              </Box>
            )
          ) : (
            (nicPorts.nics || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Нет сетевых интерфейсов с MAC. Добавьте интерфейс с MAC-адресом — и система покажет, за каким портом коммутатора он виден.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {nicPorts.nics.map((n) => (
                  <Box key={n.interface_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={n.name} />
                    {n.mac && <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{n.mac}</Typography>}
                    {n.fdb_location ? (
                      <Typography variant="body2">
                        → <b>{n.fdb_location.port}</b> на{' '}
                        <Link to={`/devices/${n.fdb_location.switch_device_id}`} style={{ color: '#1976d2' }}>{n.fdb_location.switch_name}</Link>
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">порт не определён (нет в FDB)</Typography>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    {n.status === 'confirmed' && <Chip size="small" color="success" variant="outlined" label="подтверждено FDB" />}
                    {n.status === 'mismatch' && (
                      <Tooltip title={`Записан порт «${n.connection?.recorded_port}», а FDB видит «${n.fdb_location?.port}»`}>
                        <Chip size="small" color="error" variant="outlined" label="расхождение порта" />
                      </Tooltip>
                    )}
                    {n.status === 'unrecorded' && canEditDevices && (
                      <Button size="small" variant="outlined" onClick={() => bindNicPort(n)}>Привязать</Button>
                    )}
                    {n.status === 'unrecorded' && !canEditDevices && <Chip size="small" color="warning" variant="outlined" label="не записано" />}
                  </Box>
                ))}
              </Box>
            )
          )}
        </Paper>
      )}

      {/* === СЕТЬ: активность === */}
      {pageTab === 'network' && netInfo && netInfo.present && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <MonitorIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Сетевая активность (по данным агента)</Typography>
            <Tooltip title="Что агент видит на самом ПК: IP по интерфейсам, соседи по ARP, какие порты слушает, куда идут соединения. Питает «Матрицу доступности».">
              <InfoIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            </Tooltip>
            <Box sx={{ flexGrow: 1 }} />
            <Link to="/network-matrix" style={{ color: '#1976d2', fontSize: 13 }}>Матрица доступности →</Link>
          </Box>
          {(netInfo.interfaces || []).map((it, i) => (
            <Typography key={`if-${i}`} variant="body2">
              <b style={{ fontFamily: 'monospace' }}>{it.mac}</b>: {(it.ips || []).join(', ') || '—'}
              {it.gateways && it.gateways.length ? ` · шлюз ${it.gateways.join(', ')}` : ''}
              {it.dns && it.dns.length ? ` · DNS ${it.dns.join(', ')}` : ''}
            </Typography>
          ))}
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">Слушает порты ({(netInfo.listening || []).length}):</Typography>
            <Box sx={{ mt: 0.5 }}>
              {(netInfo.listening || []).slice(0, 30).map((p, i) => (
                <Chip key={`l-${i}`} size="small" sx={{ mr: 0.5, mb: 0.5 }}
                      label={`${p.port}${p.service ? ' · ' + p.service : ''}${p.process ? ' · ' + p.process : ''}`} />
              ))}
              {(netInfo.listening || []).length === 0 && <Typography variant="body2" color="text.secondary">—</Typography>}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="caption" color="text.secondary">Соседи в сети (ARP): {(netInfo.neighbors || []).length}</Typography>
              <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                {(netInfo.neighbors || []).slice(0, 30).map((n, i) => (
                  <Typography key={`n-${i}`} variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {n.ip} · {n.mac}
                  </Typography>
                ))}
              </Box>
            </Box>
            <Box sx={{ minWidth: 220, flexGrow: 1 }}>
              <Typography variant="caption" color="text.secondary">Исходящие соединения ({(netInfo.flows || []).length}) — куда это устройство само подключается:</Typography>
              <Box sx={{ maxHeight: 120, overflow: 'auto' }}>
                {(netInfo.flows || []).slice(0, 40).map((f, i) => (
                  <Typography key={`f-${i}`} variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    → {f.raddr}:{f.rport}{f.service ? ` (${f.service})` : ''}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* === БЕЗОПАСНОСТЬ === */}
      {pageTab === 'security' && netInfo && netInfo.present && netInfo.security && Object.keys(netInfo.security).length > 0 && (() => {
        const sec = netInfo.security;
        const fw = sec.firewall || {};
        return (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <MonitorIcon fontSize="small" color="primary" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Безопасность (по данным агента)</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              {fw.enabled !== undefined && (
                <Chip size="small" color={fw.enabled ? 'success' : 'error'}
                      label={`Брандмауэр: ${fw.enabled ? 'вкл' : 'ВЫКЛ'}`} />
              )}
              {(sec.rdp_enabled === true || sec.rdp_enabled === false) && (
                <Chip size="small" variant="outlined" color={sec.rdp_enabled ? 'warning' : 'default'}
                      label={`RDP: ${sec.rdp_enabled ? 'включён' : 'выкл'}`} />
              )}
              {(sec.antivirus || []).map((a, i) => (
                <Chip key={`av-${i}`} size="small" variant="outlined" color={a.enabled === false ? 'warning' : 'success'}
                      label={`AV: ${a.name}${a.enabled === false ? ' (выкл)' : ''}${a.up_to_date === false ? ' (устарел)' : ''}`} />
              ))}
              {(sec.antivirus || []).length === 0 && (
                <Chip size="small" variant="outlined" color="warning" label="Антивирус: не найден" />
              )}
              {sec.hotfixes && sec.hotfixes.count !== undefined && (
                <Chip size="small" variant="outlined"
                      label={`Обновлений: ${sec.hotfixes.count}${sec.hotfixes.last_installed ? ' · посл. ' + sec.hotfixes.last_installed : ''}`} />
              )}
            </Box>
            {sec.logged_user && (
              <Typography variant="body2" color="text.secondary">Пользователь сейчас: <b>{sec.logged_user}</b></Typography>
            )}
            {(sec.shares || []).length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">Сетевые папки ({(sec.shares || []).length}):</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {(sec.shares || []).map((sh, i) => (
                    <Chip key={`sh-${i}`} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }}
                          color={sh.admin ? 'default' : 'primary'}
                          label={`${sh.name}${sh.path ? ' → ' + sh.path : ''}`} />
                  ))}
                </Box>
              </Box>
            )}
            {fw.profiles && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Профили брандмауэра: {Object.keys(fw.profiles).map((k) => `${k}: ${fw.profiles[k] ? 'вкл' : 'выкл'}`).join(' · ')}
              </Typography>
            )}
          </Paper>
        );
      })()}

      {/* === НАБЛЮДЕНИЕ: агент === */}
      {pageTab === 'monitoring' && (
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <MonitorIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Агент (свой клиент на ПК)</Typography>
          <Tooltip title="Лёгкая программа ставится на компьютер и сама шлёт инвентарь и метрики (диск/CPU/аптайм). Создайте токен, скачайте конфиг, поставьте агент — дальше всё автоматически.">
            <InfoIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <Button size="small" onClick={() => downloadAgent('windows', 'inventory_agent.exe')} disabled={!agentAvail.windows}>
            Скачать .exe
          </Button>
          {canEditDevices && (
            <Button size="small" onClick={() => document.getElementById('scan-upload-input').click()}>
              Загрузить скан
            </Button>
          )}
          <input id="scan-upload-input" type="file" accept=".json,application/json" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files[0]; if (f) applyScanFile(f); e.target.value = ''; }} />
          {canEditDevices && (
            <Button size="small" variant="outlined" onClick={issueAgentToken}>
              {enrollInfo?.enrolled ? 'Обновить токен' : 'Подключить агент'}
            </Button>
          )}
          {canEditDevices && enrollInfo?.enrolled && (
            <Button size="small" color="error" onClick={revokeAgentToken}>Отозвать</Button>
          )}
        </Box>
        {enrollInfo?.enrolled ? (
          <Typography variant="body2" color="text.secondary">
            Подключён · токен …{enrollInfo.last4}
            {enrollInfo.last_used_at
              ? ` · последний отчёт: ${new Date(enrollInfo.last_used_at).toLocaleString('ru-RU')}`
              : ' · отчётов ещё не было'}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Не подключён. Нажмите «Подключить агент» — система выдаст токен и готовый конфиг для этого устройства.
          </Typography>
        )}
      </Paper>
      )}

      <Dialog open={agentDlg} onClose={() => setAgentDlg(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Подключение агента к «{device.name}»</DialogTitle>
        <DialogContent>
          {(() => {
            const base = window.location.origin;
            const cfg = JSON.stringify({ url: base, token: agentToken, interval: 3600, with_software: false }, null, 2);
            const cmds = [
              '# Windows (рядом с inventory_agent.exe и inventory_agent.json):',
              'inventory_agent.exe --install',
              '',
              '# Linux (от root):',
              'sudo python3 inventory_agent.py --install --config inventory_agent.json',
              '',
              '# Разовая проверка без установки:',
              'python3 inventory_agent.py --config inventory_agent.json --dry-run',
            ].join('\n');
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Alert severity="info">
                  Скачайте агент, запустите — откроется окно настройки. Вставьте адрес и токен (ниже),
                  нажмите «Проверить связь» → «Установить службу». Дальше данные шлются сами.
                </Alert>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="contained" onClick={() => downloadAgent('windows', 'inventory_agent.exe')} disabled={!agentAvail.windows}>
                    Windows 7 / 8 / 10 / 11 (.exe)
                  </Button>
                  <Button variant="outlined" onClick={() => downloadAgent('xp', 'inventory_agent_xp.exe')} disabled={!agentAvail.xp}>
                    Windows XP / Vista (.exe)
                  </Button>
                  <Button variant="outlined" onClick={() => downloadAgent('source', 'inventory_agent.py')} disabled={!agentAvail.source}>
                    Linux (.py)
                  </Button>
                </Box>

                <Alert severity="warning" sx={{ py: 0 }}>Токен показывается только один раз — сохраните его сейчас.</Alert>
                <Box>
                  <Typography variant="caption" color="text.secondary">Адрес сервера</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField value={base} size="small" fullWidth InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }} />
                    <Button onClick={() => copyText(base)}>Копировать</Button>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Токен устройства (X-Agent-Token)</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField value={agentToken} size="small" fullWidth InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }} />
                    <Button onClick={() => copyText(agentToken)}>Копировать</Button>
                  </Box>
                </Box>

                <details>
                  <summary style={{ cursor: 'pointer', color: '#666', fontSize: 13 }}>Для опытных: конфиг-файл и команды</summary>
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>inventory_agent.json (рядом с агентом)</Typography>
                      <Button size="small" onClick={() => copyText(cfg)}>Копировать</Button>
                      <Button size="small" onClick={() => downloadConfig(cfg)}>Скачать</Button>
                    </Box>
                    <TextField value={cfg} multiline minRows={4} fullWidth InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Запуск / установка (CLI):</Typography>
                    <TextField value={cmds} multiline minRows={5} fullWidth InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }} />
                  </Box>
                </details>
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setAgentDlg(false)}>Готово</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bindDlg} onClose={() => setBindDlg(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Привязать источник наблюдения</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField select label="Источник" value={bindForm.source_id}
              onChange={(e) => setBindForm({ ...bindForm, source_id: e.target.value })} fullWidth>
              {sources.length === 0 && <MenuItem value="" disabled>Нет источников — добавьте в «Интеграции»</MenuItem>}
              {sources.map((s) => <MenuItem key={s.id} value={s.id}>{s.name} ({s.type})</MenuItem>)}
            </TextField>
            <TextField label="Внешний ID / ссылка" placeholder="GLPI id · Zabbix host · IP для SNMP · …"
              value={bindForm.external_ref} onChange={(e) => setBindForm({ ...bindForm, external_ref: e.target.value })} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBindDlg(false)}>Отмена</Button>
          <Button variant="contained" onClick={saveBinding} disabled={!bindForm.source_id}>Привязать</Button>
        </DialogActions>
      </Dialog>

      {/* === ОБЗОР: основная карточка === */}
      {pageTab === 'overview' && (
      <Grid container spacing={3} sx={{ mb: 1 }}>
        {/* Basic info card */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                {/* Device image */}
                {(device.image_path || device.device_type?.image_path) && (
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid #e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f5f5f5',
                      flexShrink: 0
                    }}
                  >
                    <img
                      src={device.image_path || `/api/device-types/${device.device_type?.id}/image`}
                      alt={device.name}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  </Box>
                )}

                {/* Basic info */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
                      Тип:
                    </Typography>
                    <Chip
                      label={device.device_type?.name || 'Не указан'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
                      Локация:
                    </Typography>
                    <Chip
                      label={device.location?.name || 'Не указана'}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CableIcon sx={{ mr: 1, color: 'info.main' }} />
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 1 }}>
                      Подключений:
                    </Typography>
                    <Chip
                      label={connections.length}
                      size="small"
                      color="info"
                      variant="outlined"
                    />
                  </Box>

                  {device.description && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Описание:
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {device.description}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* === ОБЗОР: фотографии устройства === */}
      {pageTab === 'overview' && (
        <Box sx={{ mb: 3 }}>
          <DeviceGallerySection deviceId={id} expanded={expanded.gallery} onExpandChange={handleAccordionChange('gallery')} />
        </Box>
      )}

      {/* === ХАРАКТЕРИСТИКИ (на всю ширину) === */}
      {pageTab === 'chars' && (
        <Box sx={{ mb: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <SectionHeader
                  title="Характеристики"
                  tooltip="Точный набор характеристик этого типа устройства. Диски и сетевые карты — отдельными экземплярами. Кнопка «⋮» у строки — действия с компонентом (заявка на замену/снятие, история)."
                />
                {canEditDevices && scanChars && scanChars.available && (
                  <Tooltip title="Заполнить значения характеристик данными последнего скана агента (диски/MAC/видеокарты — экземплярами).">
                    <Button size="small" variant="outlined" onClick={applyScanChars}>Обновить значения из скана</Button>
                  </Tooltip>
                )}
              </Box>
              <Divider sx={{ my: 2 }} />

              {(() => {
                const ds = (monState?.fingerprint?.disks || []).filter((d) => d.size_gb);
                if (ds.length === 0) return null;
                const COLORS = ['#1976d2', '#26a69a', '#ffa726', '#ab47bc', '#ef5350', '#8d6e63'];
                const data = ds.map((d, i) => ({ name: d.serial || `Диск ${i + 1}`, value: Math.round(d.size_gb) }));
                const total = data.reduce((s, x) => s + x.value, 0);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ width: 150, height: 150 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Pie data={data} dataKey="value" nameKey="name" innerRadius={42} outerRadius={68} paddingAngle={2}>
                            {data.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Pie>
                          <ReTooltip formatter={(v) => `${v} ГБ`} />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Накопители: {total} ГБ всего</Typography>
                      {data.map((d, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[i % COLORS.length] }} />
                          <Typography variant="body2" color="text.secondary">{d.name}: {d.value} ГБ</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })()}

              {charGroupList.length === 0 ? (
                <Typography color="text.secondary">Нет заполненных характеристик</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {charGroupList.flatMap(({ char, instances }) =>
                        instances.map((cv) => {
                          const composite = !!cv.value_json || (char.fields && char.fields.length > 1);
                          const display = cv.value || (cv.value_json ? JSON.stringify(cv.value_json) : '-');
                          const label = char.is_multiple
                            ? `${char.name} #${(cv.instance_index || 0) + 1}${cv.instance_label ? ' (' + cv.instance_label + ')' : ''}`
                            : char.name;
                          return (
                            <TableRow key={cv.id} hover>
                              <TableCell sx={{ fontWeight: 500, width: '42%', color: 'text.secondary', py: 0.5 }}>
                                {label}
                              </TableCell>
                              <TableCell sx={{ py: 0.5 }}>{display}</TableCell>
                              <TableCell sx={{ py: 0.5, whiteSpace: 'nowrap' }} align="right">
                                {canCreateCR && (
                                  <Tooltip title="Действия с компонентом">
                                    <IconButton size="small" onClick={(e) => setCompMenu({ anchorEl: e.currentTarget, char, cv, composite })}>
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* === СЕТЬ: подключения === */}
      {pageTab === 'network' && (
      <Grid container spacing={3} sx={{ mb: 1 }}>
        {/* Connections list */}
        <Grid item xs={12}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <SectionHeader
                  title={`Подключения (${connections.length})`}
                  tooltip="Список физических и логических подключений к другим устройствам: Ethernet, оптика, Wi-Fi и т.д."
                  icon={<CableIcon />}
                />
                {(user?.role?.name === 'Админ' || user?.role?.name === 'Суперадмин') && (
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setConnectionDialogOpen(true)}
                  >
                    Добавить
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              {connections.length === 0 ? (
                <Typography color="text.secondary">Нет подключений</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Устройство</TableCell>
                        <TableCell>Тип</TableCell>
                        {(user?.role?.name === 'Админ' || user?.role?.name === 'Суперадмин') && (
                          <TableCell width="50"></TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {connections.map((conn) => (
                        <TableRow key={conn.id} hover>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                              onClick={() => navigate(`/devices/${conn.other_device?.id}`)}
                            >
                              {conn.other_device?.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={conn.connection_type?.name}
                              size="small"
                              sx={{
                                backgroundColor: conn.connection_type?.color || '#888',
                                color: 'white'
                              }}
                            />
                          </TableCell>
                          {(user?.role?.name === 'Админ' || user?.role?.name === 'Суперадмин') && (
                            <TableCell>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteConnection(conn.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {canCreateCR && suggestions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SuggestIcon fontSize="small" color="primary" /> Предлагаемые связи ({suggestions.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Найдены автоматически по данным сети — проверьте и примите.
                  </Typography>
                  {suggestions.map((s, i) => {
                    const other = s.source_device_id === device.id
                      ? { id: s.target_device_id, name: s.target_name }
                      : { id: s.source_device_id, name: s.source_name };
                    const conf = { high: { c: 'success', t: 'точно' }, medium: { c: 'warning', t: 'вероятно' },
                                   low: { c: 'default', t: 'возможно' } }[s.confidence] || { c: 'default', t: s.confidence };
                    return (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                        <Chip size="small" color={conf.c} label={conf.t} />
                        <Box sx={{ flexGrow: 1, minWidth: 120 }}>
                          <Typography variant="body2">{other.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{s.reason}</Typography>
                        </Box>
                        <Button size="small" variant="outlined" onClick={() => acceptSuggestion(s)}>Принять</Button>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* === СЕТЬ: интерфейсы === */}
      {pageTab === 'network' && (
      <Accordion
        expanded={expanded.interfaces}
        onChange={handleAccordionChange('interfaces')}
        sx={{ mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SectionHeader
            title="Сетевые интерфейсы"
            tooltip="Управление сетевыми интерфейсами устройства (Ethernet, Wi-Fi и др.). Можно указать IP-адрес, маску подсети, MAC-адрес и другие параметры."
            icon={<CableIcon />}
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <NetworkInterfaceManager deviceId={id} />
        </AccordionDetails>
      </Accordion>
      )}

      {/* === ПО === */}
      {pageTab === 'software' && (
      <Accordion
        expanded={expanded.software}
        onChange={handleAccordionChange('software')}
        sx={{ mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SectionHeader
            title="Программное обеспечение"
            tooltip="Список установленного ПО на устройстве с информацией о версиях, лицензиях и ключах активации."
            icon={<CategoryIcon />}
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <DeviceSoftwareTab deviceId={id} />
        </AccordionDetails>
      </Accordion>
      )}

      {/* === БЕЗОПАСНОСТЬ: пользователи с доступом === */}
      {pageTab === 'security' && (
      <Accordion
        expanded={expanded.users}
        onChange={handleAccordionChange('users')}
        sx={{ mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SectionHeader
            title="Пользователи с доступом"
            tooltip="Управление учётными записями пользователей, имеющих доступ к данному устройству. Можно указать логины, пароли и примечания."
            icon={<EditIcon />}
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <DeviceUsersTab deviceId={id} />
        </AccordionDetails>
      </Accordion>
      )}

      {/* === СЕТЬ: карта связей === */}
      {pageTab === 'network' && (
      <Accordion
        expanded={expanded.map}
        onChange={handleAccordionChange('map')}
        sx={{ mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SectionHeader
            title="Карта связей"
            tooltip="Визуальная схема подключений устройства к соседним устройствам. Центральное устройство — текущее, вокруг — связанные устройства на расстоянии до 2 уровней"
          />
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              Кликните на устройство для перехода. Глубина:
            </Typography>
            {[1, 2, 3].map((n) => (
              <Button key={n} size="small" variant={mapLevels === n ? 'contained' : 'outlined'}
                sx={{ minWidth: 32, px: 1 }} onClick={() => { setMapLevels(n); fetchGraphData(n); }}>
                {n}
              </Button>
            ))}
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" startIcon={<CableIcon />} onClick={() => navigate(`/map-auto?device=${id}&hops=${mapLevels}`)}>
              Показать соседей на карте
            </Button>
          </Box>
          <Box sx={{ height: 420, p: 1 }}>
            <ConnectionMiniMap graphData={graphData} navigate={navigate} />
          </Box>
        </AccordionDetails>
      </Accordion>
      )}

      {/* === ЖУРНАЛ: история + метаданные === */}
      {pageTab === 'history' && (<>
      {/* Device History - only for users with audit.view permission */}
      {(user?.is_superuser || user?.permissions?.includes('audit.view')) && (
        <Box sx={{ mt: 3 }}>
          <DeviceHistorySection deviceId={id} expanded={expanded.history} onExpandChange={handleAccordionChange('history')} />
        </Box>
      )}

      {/* Metadata */}
      <Accordion
        expanded={expanded.metadata}
        onChange={handleAccordionChange('metadata')}
        sx={{ mt: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <SectionHeader
            title="Метаданные"
            tooltip="Служебная информация: дата создания записи об устройстве и дата последнего обновления"
          />
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Создано</Typography>
              <Typography variant="body2">
                {device.created_at ? new Date(device.created_at).toLocaleString('ru-RU') : '-'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Обновлено</Typography>
              <Typography variant="body2">
                {device.updated_at ? new Date(device.updated_at).toLocaleString('ru-RU') : '-'}
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      </>)}

      {/* Add Connection Dialog */}
      <Dialog
        open={connectionDialogOpen}
        onClose={() => setConnectionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Добавить подключение</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              options={allDevices}
              getOptionLabel={(option) => option.name}
              value={allDevices.find(d => d.id === newConnection.target_device_id) || null}
              onChange={(e, newValue) => {
                setNewConnection({
                  ...newConnection,
                  target_device_id: newValue?.id || null
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Устройство для подключения"
                  required
                />
              )}
            />

            <Autocomplete
              options={connectionTypes}
              getOptionLabel={(option) => option.name}
              value={connectionTypes.find(ct => ct.id === newConnection.connection_type_id) || null}
              onChange={(e, newValue) => {
                setNewConnection({
                  ...newConnection,
                  connection_type_id: newValue?.id || null
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Тип подключения"
                  required
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: option.color || '#888'
                      }}
                    />
                    {option.name}
                  </Box>
                </li>
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectionDialogOpen(false)}>
            Отмена
          </Button>
          <Button
            onClick={handleCreateConnection}
            variant="contained"
            disabled={!newConnection.target_device_id || !newConnection.connection_type_id}
          >
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Device Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Редактировать устройство</DialogTitle>
        <DialogContent>
          <Tabs value={editTabValue} onChange={(e, v) => setEditTabValue(v)}>
            <Tab label="Основное" />
            <Tab label="Характеристики" disabled={!editFormData.device_type_id} />
          </Tabs>

          {/* Tab 0: Basic Info */}
          {editTabValue === 0 && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Название *"
                fullWidth
                value={editFormData.name}
                onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              />

              <Autocomplete
                options={deviceTypes}
                getOptionLabel={(option) => option.name || ''}
                value={deviceTypes.find(dt => dt.id === editFormData.device_type_id) || null}
                onChange={(e, newValue) => {
                  setEditFormData({ ...editFormData, device_type_id: newValue?.id || '' });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Тип устройства *" />
                )}
              />

              <Autocomplete
                options={locations}
                getOptionLabel={(option) => option.name || ''}
                value={locations.find(l => l.id === editFormData.location_id) || null}
                onChange={(e, newValue) => {
                  setEditFormData({ ...editFormData, location_id: newValue?.id || null });
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Расположение" />
                )}
              />

              <TextField
                label="Описание"
                fullWidth
                multiline
                rows={3}
                value={editFormData.description}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              />
            </Box>
          )}

          {/* Tab 1: Characteristics */}
          {editTabValue === 1 && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Характеристики устройства
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {deviceTypes.find(dt => dt.id === editFormData.device_type_id)?.characteristics
                  ?.sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                  .map(dtc => renderCharacteristicInput(dtc))}
              </Grid>
              {deviceTypes.find(dt => dt.id === editFormData.device_type_id)?.characteristics?.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  У этого типа устройства нет привязанных характеристик.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Отмена</Button>
          <Button
            onClick={async () => {
              try {
                await devicesAPI.update(id, editFormData);
                setEditDialogOpen(false);
                fetchDevice(); // Reload device data
              } catch (err) {
                console.error('Error updating device:', err);
                setError('Ошибка обновления устройства');
              }
            }}
            variant="contained"
            disabled={!editFormData.name || !editFormData.device_type_id}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Меню действий с компонентом (клик по «⋮» в «Характеристиках») */}
      <Menu anchorEl={compMenu?.anchorEl} open={Boolean(compMenu)} onClose={() => setCompMenu(null)}>
        {compMenu && (!compMenu.composite || compMenu.char?.is_multiple) && (
          <MenuItem onClick={() => { openComponentCR(compMenu.char, compMenu.cv, 'replace'); setCompMenu(null); }}>
            <SwapIcon fontSize="small" style={{ marginRight: 8 }} /> Заявка на замену (со склада)
          </MenuItem>
        )}
        {compMenu && compMenu.char?.is_multiple && (
          <MenuItem onClick={() => { openComponentCR(compMenu.char, compMenu.cv, 'remove'); setCompMenu(null); }}>
            <DeleteIcon fontSize="small" style={{ marginRight: 8, color: '#d32f2f' }} /> Заявка на снятие
          </MenuItem>
        )}
        <MenuItem onClick={() => { setPageTab('history'); setCompMenu(null); }}>
          <HistoryIcon fontSize="small" style={{ marginRight: 8 }} /> История изменений
        </MenuItem>
      </Menu>

      {crInitial && (
        <CreateDialog
          initial={crInitial}
          setError={setError}
          canSubmit={user?.is_superuser || user?.permissions?.includes('change_requests.submit')}
          onClose={() => setCrInitial(null)}
          onDone={() => { setCrInitial(null); fetchDevice(); fetchMonitoringState(); }}
        />
      )}
    </Container>
  );
}

export default DeviceDetail;
